// True when the event is a bare "/" keypress outside of a text field. We deliberately do NOT
// intercept Cmd/Ctrl+F anymore so the browser's native page find keeps working; "/" focuses the
// activity filter instead.
export function isSearchShortcut(event) {
  if (event.key !== "/") return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;

  return !isEditableTarget(event.target);
}

function isEditableTarget(target) {
  if (!target || !target.tagName) return false;

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;

  return Boolean(target.isContentEditable);
}

export function focusSearchForKey(event, input) {
  if (!isSearchShortcut(event)) return false;

  event.preventDefault();
  input.focus();
  input.select();
  return true;
}

// Keep in sync with ActivityFeeds::SearchToken::MAX_QUERY_LENGTH — the server strips and truncates
// the query to this length before searching and minting the token, so the client must apply the
// same normalization when it compares the raw input against the query the results frame reports.
export const MAX_QUERY_LENGTH = 200;

// Exactly the characters Ruby's String#strip removes: NUL and ASCII whitespace (tab, LF, VT, FF, CR,
// space). We deliberately do NOT use JS trim(), which also strips Unicode spaces (e.g. NBSP) that
// the server keeps — a mismatch there would make the canonical query differ from the server's and
// wedge the deferred add-note open (noteDrawerIsStale would never compare equal).
const RUBY_STRIP_RE = /^[\x00\t\n\v\f\r ]+|[\x00\t\n\v\f\r ]+$/g;

// Match the server's `query.strip.first(MAX_QUERY_LENGTH)`: strip Ruby whitespace, then truncate by
// character (Unicode code point) — Array.from iterates code points, unlike slice() which counts
// UTF-16 code units and would split a non-BMP character at the boundary and diverge from Ruby.
export function canonicalizeQuery(value) {
  const stripped = (value || "").replace(RUBY_STRIP_RE, "");
  return Array.from(stripped).slice(0, MAX_QUERY_LENGTH).join("");
}

// True when the query typed into the input no longer matches the query the results frame was last
// rendered for — i.e. a debounced/in-flight filter has not landed yet. The add-note drawer URL is
// only re-synced on frame load, so opening the drawer while this is true would carry a stale token
// and a saved note would revert the feed. Pure so it is unit-testable without a DOM.
export function noteDrawerIsStale(inputValue, loadedQuery) {
  return canonicalizeQuery(inputValue) !== (loadedQuery || "");
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Builds a global, case-insensitive matcher for the trimmed query, or null when there is
// nothing meaningful to highlight. Mirrors the server-side ILIKE substring search.
export function buildHighlightRegex(query) {
  const trimmed = (query || "").trim();
  if (!trimmed) return null;

  return new RegExp(escapeRegExp(trimmed), "gi");
}

// Splits a text string into ordered segments, marking which ones matched the query. Returns a
// single unmatched segment when the query is blank or absent so callers can treat every text node
// uniformly. Pure (no DOM) so it is unit-testable on its own.
export function splitTextForHighlight(text, query) {
  const source = text || "";
  const regex = buildHighlightRegex(query);
  if (!regex || source === "") return [{ text: source, match: false }];

  const segments = [];
  let lastIndex = 0;
  let result;
  while ((result = regex.exec(source)) !== null) {
    if (result.index > lastIndex) {
      segments.push({ text: source.slice(lastIndex, result.index), match: false });
    }
    segments.push({ text: result[0], match: true });
    lastIndex = result.index + result[0].length;

    // Guard against zero-length matches looping forever (defensive; query is non-empty here).
    if (result[0].length === 0) regex.lastIndex += 1;
  }

  if (lastIndex < source.length) {
    segments.push({ text: source.slice(lastIndex), match: false });
  }

  return segments;
}
